import { Inject, Injectable, Optional, TransferState, makeStateKey } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PARTNER_TOKEN } from 'tockens';
import { Partner } from '@app/models/partner/IPartner';
import { EncryptionService } from './encyption.service';
import { PartnerService } from './partner.service';

const PARTNER_STATE_KEY = makeStateKey<any>('partnerdata');
const DEFAULT_PARTNER_ID = '';
const DEFAULT_RECAPTCHA_SITE_KEY = '6LfRh1IeAAAAAHtWRke9B5YG-QTnu3HpBhzAckg0';

@Injectable({
    providedIn: 'root',
})
export class ConfigService {
    recaptchaSiteKeyV3 = DEFAULT_RECAPTCHA_SITE_KEY;

    constructor(
        private readonly partnerService: PartnerService,
        private readonly encryptionService: EncryptionService,
        private readonly transferState: TransferState,
        @Optional() @Inject(PARTNER_TOKEN) private readonly injectedPartnerId: string | null,
    ) { }

    /**
     * Runs before bootstrap so RECAPTCHA_V3_SITE_KEY is created with the
     * current partner's key. This service intentionally does not inject
     * AuthGuardService, which itself consumes ConfigService.
     */
    async loadConfig(): Promise<void> {
        const cachedResponse = this.transferState.get(PARTNER_STATE_KEY, null);
        const cachedPartner = this.decodePartner(cachedResponse);
        if (cachedPartner) {
            this.setRecaptchaSiteKey(cachedPartner);
            return;
        }

        const partnerId = this.getPartnerId();
        if (!partnerId) {
            return;
        }

        const response = await firstValueFrom(
            this.partnerService.request({ partnerid: partnerId }, 'checkpartner'),
        );
        const partner = this.decodePartner(response);
        if (!partner) {
            return;
        }

        // AppComponent consumes the same state key, avoiding a second partner
        // request during the initial server render and browser hydration.
        this.transferState.set(PARTNER_STATE_KEY, response);
        this.setRecaptchaSiteKey(partner);
    }

    private getPartnerId(): string | null {
        return this.injectedPartnerId || DEFAULT_PARTNER_ID;
    }

    private decodePartner(response: any): Partner | null {
        if (!response?.data || typeof response.data !== 'string' || response.data.length <= 16) {
            return null;
        }

        try {
            const token = response.data.slice(-16);
            const encryptedPartner = response.data.slice(0, -16);
            const partner = JSON.parse(this.encryptionService.deencrypt(token, encryptedPartner));
            return partner?.partnerid ? partner as Partner : null;
        } catch {
            return null;
        }
    }

    private setRecaptchaSiteKey(partner: Partner): void {
        this.recaptchaSiteKeyV3 = partner.recaptchaSiteKeyV3 || DEFAULT_RECAPTCHA_SITE_KEY;
    }
}
